/**
 * BiomarkerEntryForm Component
 *
 * Editable form for entering lab result biomarker values.
 * Pre-populated with TRT panel, supports custom markers.
 */
import { useState } from 'react'
import {
  Card,
  Table,
  InputNumber,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Typography,
} from 'antd'
import { PlusOutlined, DeleteOutlined, ExperimentOutlined } from '@ant-design/icons'
import {
  BiomarkerValue,
  BiomarkerCategory,
  calculateBiomarkerStatus,
  getStatusColor,
  getStatusLabel,
  groupBiomarkersByCategory,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
} from '../lib/biomarker-config'

const { Text, Title } = Typography

interface BiomarkerEntryFormProps {
  values: BiomarkerValue[]
  onValuesChange: (values: BiomarkerValue[]) => void
  disabled?: boolean
}

interface AddCustomMarkerFormValues {
  code: string
  name: string
  unit: string
  referenceMin?: number
  referenceMax?: number
  category: BiomarkerCategory
}

/**
 * Status indicator component
 */
function StatusIndicator({ status }: { status: BiomarkerValue['status'] }) {
  if (!status) {
    return <Text type="secondary">-</Text>
  }
  return <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
}

/**
 * Reference range display
 */
function ReferenceRange({
  min,
  max,
  unit,
}: {
  min?: number
  max?: number
  unit: string
}) {
  if (min === undefined || max === undefined) {
    return <Text type="secondary">-</Text>
  }
  return (
    <Text type="secondary" style={{ fontSize: 12 }}>
      {min} - {max} {unit}
    </Text>
  )
}

/**
 * Single biomarker category table
 */
function CategoryTable({
  category,
  biomarkers,
  onValueChange,
  onDelete,
  disabled,
}: {
  category: BiomarkerCategory
  biomarkers: BiomarkerValue[]
  onValueChange: (code: string, value: number | null) => void
  onDelete?: (code: string) => void
  disabled?: boolean
}) {
  if (biomarkers.length === 0) {
    return null
  }

  const columns = [
    {
      title: 'Marker',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name: string, record: BiomarkerValue) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.code}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Vrednost',
      dataIndex: 'value',
      key: 'value',
      width: 120,
      render: (value: number | null, record: BiomarkerValue) => (
        <InputNumber
          value={value}
          onChange={(v) => onValueChange(record.code, v)}
          placeholder="-"
          style={{ width: 100 }}
          disabled={disabled}
          step={0.1}
          precision={2}
        />
      ),
    },
    {
      title: 'Jedinica',
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      render: (unit: string) => <Text type="secondary">{unit}</Text>,
    },
    {
      title: 'Ref. opseg',
      key: 'reference',
      width: 120,
      render: (_: unknown, record: BiomarkerValue) => (
        <ReferenceRange
          min={record.referenceMin}
          max={record.referenceMax}
          unit={record.unit}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: BiomarkerValue['status']) => <StatusIndicator status={status} />,
    },
    ...(onDelete
      ? [
          {
            title: '',
            key: 'actions',
            width: 50,
            render: (_: unknown, record: BiomarkerValue) =>
              record.isCustom ? (
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(record.code)}
                  disabled={disabled}
                />
              ) : null,
          },
        ]
      : []),
  ]

  return (
    <div style={{ marginBottom: 24 }}>
      <Title level={5} style={{ marginBottom: 12 }}>
        {CATEGORY_LABELS[category]}
      </Title>
      <Table
        columns={columns}
        dataSource={biomarkers}
        rowKey="code"
        pagination={false}
        size="small"
        bordered
      />
    </div>
  )
}

/**
 * Add Custom Marker Modal
 */
function AddCustomMarkerModal({
  open,
  onCancel,
  onAdd,
  existingCodes,
}: {
  open: boolean
  onCancel: () => void
  onAdd: (marker: BiomarkerValue) => void
  existingCodes: Set<string>
}) {
  const [form] = Form.useForm<AddCustomMarkerFormValues>()

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const code = values.code.toUpperCase().replace(/\s+/g, '_')
      if (existingCodes.has(code)) {
        form.setFields([
          { name: 'code', errors: ['Marker sa ovim kodom već postoji'] },
        ])
        return
      }

      const marker: BiomarkerValue = {
        code,
        name: values.name,
        unit: values.unit,
        referenceMin: values.referenceMin,
        referenceMax: values.referenceMax,
        category: values.category || 'other',
        value: null,
        isCustom: true,
      }

      onAdd(marker)
      form.resetFields()
    })
  }

  return (
    <Modal
      title="Dodaj prilagođeni marker"
      open={open}
      onCancel={() => {
        form.resetFields()
        onCancel()
      }}
      onOk={handleSubmit}
      okText="Dodaj"
      cancelText="Odustani"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="code"
          label="Kod markera"
          rules={[{ required: true, message: 'Unesite kod markera' }]}
        >
          <Input placeholder="npr. VITAMIN_D" />
        </Form.Item>
        <Form.Item
          name="name"
          label="Naziv"
          rules={[{ required: true, message: 'Unesite naziv markera' }]}
        >
          <Input placeholder="npr. Vitamin D" />
        </Form.Item>
        <Form.Item
          name="unit"
          label="Jedinica mere"
          rules={[{ required: true, message: 'Unesite jedinicu mere' }]}
        >
          <Input placeholder="npr. ng/mL" />
        </Form.Item>
        <Space style={{ width: '100%' }}>
          <Form.Item name="referenceMin" label="Ref. minimum" style={{ marginBottom: 0 }}>
            <InputNumber placeholder="Min" style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="referenceMax" label="Ref. maksimum" style={{ marginBottom: 0 }}>
            <InputNumber placeholder="Max" style={{ width: 100 }} />
          </Form.Item>
        </Space>
        <Form.Item
          name="category"
          label="Kategorija"
          initialValue="other"
          style={{ marginTop: 16 }}
        >
          <Select>
            {CATEGORY_ORDER.map((cat) => (
              <Select.Option key={cat} value={cat}>
                {CATEGORY_LABELS[cat]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  )
}

/**
 * Main BiomarkerEntryForm Component
 */
export function BiomarkerEntryForm({
  values,
  onValuesChange,
  disabled = false,
}: BiomarkerEntryFormProps) {
  const [addModalOpen, setAddModalOpen] = useState(false)

  const grouped = groupBiomarkersByCategory(values)
  const existingCodes = new Set(values.map((v) => v.code))

  const handleValueChange = (code: string, newValue: number | null) => {
    const updatedValues = values.map((v) => {
      if (v.code === code) {
        const status = calculateBiomarkerStatus(newValue, v.referenceMin, v.referenceMax)
        return { ...v, value: newValue, status }
      }
      return v
    })
    onValuesChange(updatedValues)
  }

  const handleDeleteCustom = (code: string) => {
    const updatedValues = values.filter((v) => v.code !== code)
    onValuesChange(updatedValues)
  }

  const handleAddCustomMarker = (marker: BiomarkerValue) => {
    onValuesChange([...values, marker])
    setAddModalOpen(false)
  }

  // Count filled markers
  const filledCount = values.filter((v) => v.value !== null).length
  const totalCount = values.length

  return (
    <Card
      title={
        <Space>
          <ExperimentOutlined />
          <span>Biomarkeri</span>
          <Tag>
            {filledCount}/{totalCount}
          </Tag>
        </Space>
      }
      extra={
        !disabled && (
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
          >
            Dodaj marker
          </Button>
        )
      }
    >
      {CATEGORY_ORDER.map((category) => (
        <CategoryTable
          key={category}
          category={category}
          biomarkers={grouped[category]}
          onValueChange={handleValueChange}
          onDelete={handleDeleteCustom}
          disabled={disabled}
        />
      ))}

      {filledCount === 0 && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text type="secondary">Unesite vrednosti biomarkera iz dokumenta</Text>
        </div>
      )}

      <AddCustomMarkerModal
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onAdd={handleAddCustomMarker}
        existingCodes={existingCodes}
      />
    </Card>
  )
}

export default BiomarkerEntryForm
